import type { CogniPrintProfile, ProfileSummary, DiagnosticDimensionKey } from '../types/cogniprint';
import { DIAGNOSTIC_DIMENSION_KEYS } from '../types/cogniprint';
import { isAssessed } from '../utils/profile-validation';
import { callClaude } from './claude';
import {
  PE_002_SYSTEM_PROMPT,
  PE_004_SYSTEM_PROMPT,
  buildPE002UserMessage,
  buildPE004UserMessage,
} from '../ai/prompts/profile-engine';

// Transform snake_case AI output to camelCase ProfileSummary
function transformSummary(raw: Record<string, unknown>): ProfileSummary {
  const highlights = (raw.highlight_dimensions ?? raw.highlightDimensions) as Array<Record<string, string>> | undefined;
  return {
    summaryText: (raw.summary_text ?? raw.narrative_text ?? raw.summaryText ?? '') as string,
    upgradeHook: (raw.upgrade_hook ?? raw.upgradeHook ?? null) as string | null,
    highlightDimensions: (highlights ?? []).map((h) => ({
      dimension: h.dimension ?? '',
      highlightType: (h.highlight_type ?? h.highlightType ?? 'notable') as 'strength' | 'notable',
      displayValue: h.display_value ?? h.displayValue ?? '',
    })),
    descriptiveLabel: (raw.descriptive_label ?? raw.descriptiveLabel ?? '') as string,
  };
}

/**
 * Hand the narrator only what the learner actually told us.
 *
 * This is the guard the live defect most needed. On 2026-06-17 a profile was saved
 * in which every free dimension sat on its hard-coded fallback, and PE-004 dutifully
 * narrated those invented centre values back as an insight — "you don't lock into one
 * format, one approach, or one depth level… which is an asset". The narrator was not
 * wrong; it was told a made-up profile was real.
 *
 * A dimension whose provenance is `defaulted` or `not_assessed` is stripped before the
 * narrator sees it, so the worst it can do is say less.
 */
function narratableProfile(profile: CogniPrintProfile): Record<string, unknown> {
  const out: Record<string, unknown> = { ...profile };
  // Raw item responses are not narration material and only add tokens.
  delete out.itemResponses;

  const omitted: string[] = [];
  for (const key of DIAGNOSTIC_DIMENSION_KEYS as DiagnosticDimensionKey[]) {
    if (!isAssessed(profile, key)) {
      delete out[key];
      omitted.push(key);
    }
  }
  out.dimensionsNotAssessed = omitted;
  return out;
}

export async function generateProfileSummary(
  profile: CogniPrintProfile,
  displayName: string,
  /**
   * The learner's own words about what the last narrative got wrong. Changes the
   * WORDING only. The numbers the narrator sees are the same numbers; a note can
   * never move a score.
   */
  learnerNote?: string,
): Promise<ProfileSummary> {
  const isQuick = profile.diagnosticType === 'quick';
  const systemPrompt = isQuick ? PE_002_SYSTEM_PROMPT : PE_004_SYSTEM_PROMPT;
  const narratable = JSON.stringify(narratableProfile(profile), null, 2);
  const base = isQuick
    ? buildPE002UserMessage(narratable, displayName)
    : buildPE004UserMessage(narratable, displayName);
  const userMessage = learnerNote?.trim()
    ? `${base}

The learner read a previous version of this summary and said: "${learnerNote.trim()}"
Rewrite with that in mind — different emphasis, different examples, the same underlying scores. Do not change any score, do not invent a trait the scores do not support, and do not mention that this is a rewrite.`
    : base;

  const response = await callClaude(systemPrompt, [{ role: 'user', content: userMessage }], {
    model: 'haiku',
    maxTokens: 1024,
  });

  // Extract JSON from the response (may be wrapped in markdown code block)
  const jsonMatch = response.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Profile summary response did not contain valid JSON');
  }

  const raw = JSON.parse(jsonMatch[0]);
  return transformSummary(raw);
}
